import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { GetHelper, payloadsEqual } from 'src/app/helpers/utils';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SignupService } from 'src/app/services/signup/signup.service';
import { ToastService } from 'src/app/services/toast/toast.service';
import UIDefaultTheme from 'src/themes';

export type PersonDeclarationsValues = {
  authorized_attorney_representative: boolean;

  politically_exposed_person_relationship: boolean;
  pepExplain: string;

  pepRelationship: boolean;
  document_number: string;
  name: string;
  bond: string;

  third_party_operation: boolean;
  document_number_person_or_company: string;
  name_or_company_name: string;

  linked_cm_capital: boolean;

  politically_exposed: boolean;
  description_politically: string;

  declare_and_authorize: boolean;
  id: number;
};

@Component({
  selector: 'app-signup-unified-person-declarations',
  templateUrl: './signup-unified-person-declarations.component.html',
  styleUrls: ['./signup-unified-person-declarations.component.scss'],
})
export class SignupUnifiedPersonDeclarationsComponent
  implements OnInit, OnDestroy
{
  loading = true;
  companyShortName = UIDefaultTheme.brand.name;

  form = this.formBuilder.group({
    authorized_attorney_representative: [false, []],

    politically_exposed_person_relationship: [false, []],
    pepExplain: [null, []],

    politically_exposed: [false, []],
    description_politically: [null, []],

    third_party_operation: [false, []],
    document_number_person_or_company: [null, []],
    name_or_company_name: [null, []],

    linked_cm_capital: [false, []],

    declare_and_authorize: [false, [Validators.requiredTrue]],
  });

  pepForm = this.formBuilder.group({
    document_number: [null, []],
    name: [null, []],
    bond: [null, []],
  });

  columns: TableColumn<any>[] = [
    {
      name: 'CPF',
      cell: (row) => row.document_number,
    },
    { name: 'Nome', cell: (row) => row.name },
    {
      name: 'Vínculo',
      cell: (row) => row.bond,
    },
  ];

  data: PersonDeclarationsValues;

  pepData = [];
  event: any;

  /** Snapshot do payload após o GET; usado para evitar PATCH/PUT quando nada mudou. */
  private initialPayload: Record<string, any> | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private signupService: SignupService,
    private api: ApiService,
    private toast: ToastService,
    private route: ActivatedRoute,
    private authService: AuthService,
  ) {}

  ngOnDestroy(): void {
    this.event.unsubscribe();
  }

  ngOnInit(): void {
    this.event = this.signupService.sendPersonData.subscribe((value) => {
      if (value === 'declarations') {
        this.sendData();
      }
    });

    this.getData();

    this.form.statusChanges.subscribe((status) => this.changeFormStatus());

    this.form.controls.politically_exposed.valueChanges.subscribe((value) => {
      if (value) {
        this.form.controls.description_politically.setValidators([
          Validators.required,
        ]);
      } else {
        this.form.controls.description_politically.setValue(null);
        this.form.controls.description_politically.clearValidators();
      }
      this.form.controls.description_politically.updateValueAndValidity();
    });

    this.form.controls.third_party_operation.valueChanges.subscribe((value) => {
      if (value) {
        this.form.controls.document_number_person_or_company.setValidators([
          Validators.required,
        ]);
        this.form.controls.name_or_company_name.setValidators([
          Validators.required,
        ]);
      } else {
        this.form.controls.document_number_person_or_company.setValue(null);
        this.form.controls.name_or_company_name.setValue(null);
        this.form.controls.document_number_person_or_company.clearValidators();
        this.form.controls.name_or_company_name.clearValidators();
      }
      this.form.controls.document_number_person_or_company.updateValueAndValidity();
      this.form.controls.name_or_company_name.updateValueAndValidity();
    });

    this.form.controls.politically_exposed_person_relationship.valueChanges.subscribe(
      (value) => {
        if (value) {
          this.pepForm.controls.document_number.setValidators([
            Validators.required,
          ]);
          this.pepForm.controls.name.setValidators([Validators.required]);
          this.pepForm.controls.bond.setValidators([Validators.required]);
        } else {
          this.pepForm.controls.document_number.setValue(null);
          this.pepForm.controls.name.setValue(null);
          this.pepForm.controls.bond.setValue(null);
          this.pepForm.controls.bond.clearValidators();
          this.pepForm.controls.name.clearValidators();
          this.pepForm.controls.document_number.clearValidators();
        }
        this.pepForm.controls.document_number.updateValueAndValidity();
        this.pepForm.controls.name.updateValueAndValidity();
        this.pepForm.controls.bond.updateValueAndValidity();

        this.changeFormStatus();
      },
    );
  }

  async getData() {
    this.loading = true;
    try {
      // Pega o register_uuid da rota (obrigatório, sem fallback)
      const routeId = this.route.snapshot.paramMap.get('id');

      if (!routeId) {
        this.toast.show(
          'error',
          'Erro',
          'Register UUID não encontrado na rota',
        );
        return;
      }

      const { data } = await this.api.get({
        route: 'api/registration/declaration-authorization/',
        params: { register_uuid: routeId },
        token: true,
      });

      // Se retornar array, pegar sempre o último item
      const declarationData =
        Array.isArray(data) && data.length > 0
          ? data[data.length - 1] // Pega o último item do array
          : data;

      if (!declarationData) {
        this.initialPayload = null;
        return;
      }

      this.form.patchValue(declarationData);

      this.data = declarationData;

      if (declarationData.politically_exposed_person_relationship) {
        const arr = [];
        for (let i = 0; i < declarationData.name.length; i++) {
          arr.push({
            document_number: declarationData.document_number[i],
            name: declarationData.name[i],
            bond: declarationData.bond[i],
          });
        }
        this.pepData = arr;
      }

      this.initialPayload = this.buildPayloadFromForm();
      this.changeFormStatus();
    } catch (error) {
      this.initialPayload = null;
      if (error.status !== 404) {
        this.toast.show(
          'error',
          'Erro',
          error.error?.message || 'Erro ao carregar declarações',
        );
      }
    } finally {
      this.loading = false;
    }
  }

  private buildPayloadFromForm(): Record<string, any> {
    const valuesHelper = new GetHelper(this.form.value);
    return {
      authorized_attorney_representative: valuesHelper.get(
        'authorized_attorney_representative',
      ),
      politically_exposed_person_relationship: valuesHelper.get(
        'politically_exposed_person_relationship',
      ),
      pepExplain: valuesHelper.get('pepExplain'),
      name: this.pepData.map((item) => item.name),
      document_number: this.pepData.map((item) => item.document_number),
      bond: this.pepData.map((item) => item.bond),
      third_party_operation: valuesHelper.get('third_party_operation'),
      document_number_person_or_company: valuesHelper.get(
        'document_number_person_or_company',
      ),
      name_or_company_name: valuesHelper.get('name_or_company_name'),
      linked_cm_capital: valuesHelper.get('linked_cm_capital'),
      declare_and_authorize: valuesHelper.get('declare_and_authorize'),
      politically_exposed: valuesHelper.get('politically_exposed'),
      description_politically: valuesHelper.get('description_politically'),
      is_exempt: false,
    };
  }

  async sendData() {
    const routeId = this.route.snapshot.paramMap.get('id');

    if (!routeId) {
      this.toast.show(
        'error',
        'Erro',
        'UUID do registro não encontrado na URL',
      );
      return;
    }

    const payload = this.buildPayloadFromForm();

    if (payloadsEqual(payload, this.initialPayload ?? undefined)) {
      this.signupService.loading = false;
      await this.signupService.updateUserStepPF(
        'register',
        'analysis',
        routeId,
      );
      return;
    }

    this.signupService.loading = true;

    try {
      if (this.data && this.data.id) {
        await this.api.put({
          route: `api/registration/declaration-authorization/${this.data.id}/`,
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      } else {
        await this.api.post({
          route: 'api/registration/declaration-authorization/',
          params: { register_uuid: routeId },
          token: true,
          body: payload,
        });
      }

      this.initialPayload = this.buildPayloadFromForm();
      await this.signupService.updateUserStepPF(
        'register',
        'analysis',
        routeId,
      );
      this.toast.show('info', 'Sucesso', 'Dados salvos com sucesso!');
    } catch (error) {
      this.toast.show(
        'error',
        'Erro',
        error?.error?.message || 'Erro ao salvar os dados',
      );
      this.signupService.loading = false;
    } finally {
      this.signupService.loading = false;
    }
  }

  addPep() {
    const docAlreadyExists = this.pepData.find(
      (item) => item.document_number === this.pepForm.value.document_number,
    );

    if (docAlreadyExists) {
      this.toast.show('error', 'Erro', 'CPF já cadastrado!');
      return;
    }

    this.pepData.push(this.pepForm.value);
    this.pepForm.reset();

    this.changeFormStatus();
  }

  getDisabled() {
    return !this.pepForm.valid;
  }

  removePep(index) {
    this.pepData.splice(index, 1);

    this.changeFormStatus();
  }

  changeFormStatus() {
    const status = this.form.status;

    this.signupService.setPersonFormStatus(
      'declarations',
      this.form.controls.politically_exposed_person_relationship.value === true
        ? status === 'VALID' && this.pepData.length > 0
        : status === 'VALID',
    );
  }
}
